import graphql from 'babel-plugin-relay/macro';
import {FormikHelpers} from 'formik';
import React from 'react';
import {useMutation} from 'react-relay/hooks';
import {useHistory} from 'react-router';
import ConnectionHandler from 'relay-connection-handler-plus';
import {showEventNotification} from 'src/utils/notifications';
import {withSuspense} from 'src/utils/withSuspense';

import Form from '../components/form';
import {FormValues} from '../types';
import {createPageCreateValveMutation} from './__generated__/createPageCreateValveMutation.graphql';

function CreatePage() {
  const history = useHistory();

  const [commit, isInFlight] = useMutation<createPageCreateValveMutation>(createValveMutation);

  return (
    <Form
      title="Create Valve"
      button="Create Valve"
      onSubmit={handleSubmit}
      isSubmitting={isInFlight}
    />
  );

  function handleSubmit(input: FormValues, formikHelpers: FormikHelpers<FormValues>) {
    commit({
      variables: {
        input: {
          coordinates: [
            parseFloat(input.coordinates.longitude),
            parseFloat(input.coordinates.latitude),
          ],
          valveConnectorId: input.valveConnectorId,
          modelId: input.modelId,
          serial: input.serial,
          externalId: input.externalId,
          notes: input.notes,
          status: input.status,
          turnsToOpenClose: input.turnsToOpenClose ? Number(input.turnsToOpenClose) : undefined,
          installedAt: input.installedAt.toISOString(),
          warrantyExpiresAt: input.warrantyExpiresAt.toISOString(),
          lastServicedAt: input.lastServicedAt.toISOString(),
        },
      },
      onCompleted: payload => {
        if (payload.createValve?.errors[0] && payload.createValve?.errors[0].fields) {
          payload.createValve?.errors[0].fields.forEach(fieldError => {
            formikHelpers.setFieldError(fieldError.fieldName, fieldError.message);
          });
        }

        const entry = payload.createValve?.valve;

        if (entry) {
          showEventNotification('added', 'Valve', entry.serial);
          return history.push('/admin/valves');
        }
      },
      updater: store => {
        const root = store.getRoot();

        const connectionRecords = ConnectionHandler.getConnections(root, 'ValvesList_valves');

        if (!connectionRecords) {
          return;
        }

        const payload = store.getRootField('createValve');

        if (!payload) {
          return;
        }

        const newRecord = payload.getLinkedRecord('valve');

        if (!newRecord) {
          return;
        }

        connectionRecords.forEach(connectionRecord => {
          const newEdge = ConnectionHandler.createEdge(
            store,
            connectionRecord,
            newRecord,
            'valveEdge',
          );

          ConnectionHandler.insertEdgeAfter(connectionRecord, newEdge);
        });
      },
    });
  }
}

const createValveMutation = graphql`
  mutation createPageCreateValveMutation($input: CreateValveInput!) {
    createValve(input: $input) {
      valve {
        id
        createdAt
        serial
        externalId
        status
        turnsToOpenClose
        model {
          id
          name
        }
        family {
          id
          name
        }
        manufacturer {
          id
          name
        }
      }
      errors {
        code
        fields {
          fieldName
          message
        }
      }
    }
  }
`;

export default withSuspense(CreatePage);
